import * as React from "react";
import * as Highcharts from 'highcharts';
import HighchartsExporting from 'highcharts/modules/exporting';
import factory from 'highcharts/modules/export-data';
import HighchartsReact from 'highcharts-react-official';
import IResultInhibitor from "../../interfaces/frontend/IResultInhibitor";

if (typeof Highcharts === 'object') {
  HighchartsExporting(Highcharts);
  factory(Highcharts);
}

export interface IBaseProps {
  title?: string,
  mutation: string,
  inhibitors: IResultInhibitor[],
  selectedInhibitor: IResultInhibitor | null,
  setSelectedInhibitor (selectedInhibitor: IResultInhibitor | null): void,
  min?: number,
  max?: number,
}

export interface IBasePoint extends IResultInhibitor {
  inhibitor: IResultInhibitor,
}

// Note: Highcharts modifies data items! That's why we cannot directly use
// this.props.inhibitors but have to create point objects in getData() function.

export class InhibitorAbstractChart<
  P extends IBasePoint = IBasePoint,
  T extends IBaseProps = IBaseProps
> extends React.PureComponent<T> {

  chart: Highcharts.Chart | undefined;

  createPoint = (_: IResultInhibitor): P => {
    throw Error("You have to implement the method createPoint!");
  }

  getData = (createPoint?: (_: IResultInhibitor) => P): P[] => (
    this.props.inhibitors.map(createPoint ? createPoint : this.createPoint).filter(p => p !== null)
  )

  getOptions(): Highcharts.Options {
    throw Error("You have to implement the method getOptions!");
  }

  handleSelect = (ev) => {
    const point: P = ev.target;
    // console.debug(this.constructor.name, 'handleSelect:', point.name);
    // In case user clicks on a point in chart with multiple series,
    // while multiple series are active at the moment,
    // selection might be actually staging (!point.selected && point.selectedStaging),
    // which might lead to a situation that this point is actually selected only after
    // the point in the other series is selected (through refreshSelectionInSeries).
    // The problem is that the select event generated by click (without Shift) has accumulate=false,
    // and so unselect event would be generated for the point in the other series which overtook
    // selection of this event.
    // Therefore, setTimeout is used to let this select event finish.
    setTimeout(() => {
      if (this.props.selectedInhibitor !== (point as P).inhibitor) {
        // console.debug(this.constructor.name, 'handleSelect: condition met!');
        if ((point as any).selected === false && (point as any).selectedStaging === true) {
          console.warn(
            'handleSelect: point has not actually been selected, selection may behave unexpectedly',
            'selected:', (point as any).selected,
            'selectedStaging', (point as any).selectedStaging
          );
        }
        const inh = (point as P).inhibitor;
        this.props.setSelectedInhibitor(inh);
      }
    }, 0);
  }

  handleUnselect = (ev) => {
    const point: P = ev.target;
    // console.debug(this.constructor.name, 'handleUnselect:', point.name);
    if (this.props.selectedInhibitor === point.inhibitor) {
      // console.debug(this.constructor.name, 'handleUnselect: condition met!');
      this.props.setSelectedInhibitor(null);
    }
  }

  handleShow = (ev) => {
    const series: Highcharts.Series = ev.target;
    // in case that series is going to be rendered (animated), point wouldn't get selected;
    // therefore, we use setTimeout to let series be rendered firstly
    setTimeout(() => {
      if ((series as any).hasRendered !== true) {
        console.warn('handleShow: series has not been rendered (animated) yet, point representing selected inhibitor will not get selected', (series as any).hasRendered);
      }
      this.refreshSelectionInSeries(series);
    }, 0);
  }

  refreshSelectionInChart() {
    this.chart!.series.forEach(this.refreshSelectionInSeries);
  }

  refreshSelectionInSeries = (series: Highcharts.Series) => {
    // find point in the current series which represents selected inhibitor
    const point = this.props.selectedInhibitor !== null ? Highcharts.find(series.points, (p: P) => this.props.selectedInhibitor === p.inhibitor) : undefined;
    // get all selected points within the current series
    const selectedPoints = this.chart!.getSelectedPoints().filter(p => p.series === series);
    // determine which points should be unselected (all except the point representing selected inhibitor)
    const pointsToUnselect = point !== undefined ? selectedPoints.filter(
      (p: Highcharts.Point) => (p as unknown as P).inhibitor !== (point as unknown as P).inhibitor
    ) : selectedPoints;
    // unselect points
    pointsToUnselect.forEach(p => p.select(false));
    // select the point if it's not already selected
    if (point && !selectedPoints.includes(point)) {
      // We need to use `!selectedPoints.includes(point)` instead of just `!point.selected`,
      // because `point.selected` may be false even after its selection event is done.
      // Point object has (not documented) property 'selectedStaging' which is true in such case,
      // so `!point.selected && !point.selectedStaging` would work but it's not a public API.
      // console.debug(this.constructor.name, series.index, 'refreshSelectionInChart: selecting', point.name);
      point.select(true, true);
    }
  }

  afterAnimate = () => {
    // add title elements into legend
    if (this.chart && this.chart.container) {
      const allItems = this.chart.container.getElementsByClassName("highcharts-legend-item");
      for (let i = 0; i < allItems.length; i++) {
        const item = allItems.item(i)!;
        const allTextEls = item.getElementsByTagName("text");
        if (allTextEls.length === 1) {
          const textEl = allTextEls[0];
          if (textEl.childElementCount == 0) {
            let node = document.createElementNS("http://www.w3.org/2000/svg", "title");
            let textnode = document.createTextNode("Click to hide or show");
            node.appendChild(textnode);
            textEl.appendChild(node);
          } else if (textEl.childElementCount != 1) {
            console.warn("Number of children of text element is", textEl.childElementCount);
          }
        } else {
          console.warn("Number of text elements in highcharts-legend-item is", allTextEls.length);
        }
      }
    }
  }

  componentDidUpdate(prevProps: T) {
    if (prevProps.selectedInhibitor !== this.props.selectedInhibitor) {
      this.refreshSelectionInChart();
    }
  }

  chartCreated = (chart: Highcharts.Chart) => {
    this.chart = chart;
    if (this.props.selectedInhibitor !== null) {
      this.refreshSelectionInChart();
    }
  }

  render() {
    return <>
      <HighchartsReact
        highcharts={Highcharts}
        options={this.getOptions()}
        callback={this.chartCreated}
      />
    </>
  }
}
